import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../i18n";

export default function About() {
  const { t } = useLanguage();
  const sideCards = t("about.sideCards", []);
  const historyCards = t("about.historyCards", []);
  const structureItems = t("about.structureItems", []);
  const purposePoints = t("about.purposePoints", []);
  const paragraphs = t("about.whoWeAreParagraphs", []);

  return (
    <main className="font-sans-clean bg-[#FDF8F3] text-slate-900">
      <section className="relative overflow-hidden bg-[#5b2229] py-24 sm:py-28">
        <div className="absolute inset-0 opacity-20 ethiopian-cross" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#C9A227] mb-4">
            {t("about.eyebrow")}
          </p>
          <h1 className="font-serif-elegant text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {t("about.title")}
          </h1>
          <p className="max-w-4xl text-lg sm:text-xl leading-relaxed text-white/80">
            {t("about.intro")}
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-serif-elegant text-3xl sm:text-4xl font-bold text-[#722F37] mb-6">
                {t("about.whoWeAreTitle")}
              </h2>
              <div className="space-y-5 text-gray-700 leading-8">
                {paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative bg-[#f7f1e8] rounded-[2rem] p-8 border border-[#C9A227]/20"
            >
              <p className="text-sm uppercase tracking-[0.24em] text-[#722F37]/70 mb-4">
                {t("about.sideTitle")}
              </p>
              <div className="space-y-4 text-gray-700">
                {sideCards.map((card) => (
                  <div key={card.title} className="rounded-2xl bg-white p-5 border border-[#C9A227]/15">
                    <h3 className="font-serif-elegant text-xl font-bold text-[#722F37] mb-2">
                      {card.title}
                    </h3>
                    <p>{card.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#f7f1e8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-serif-elegant text-3xl sm:text-4xl font-bold text-[#722F37] mb-10">
            {t("about.historyTitle")}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {historyCards.map((card) => (
              <article
                key={card.title}
                className="rounded-3xl bg-white border border-[#C9A227]/10 shadow-sm p-8"
              >
                <h3 className="font-serif-elegant text-2xl font-bold text-[#722F37] mb-4">
                  {card.title}
                </h3>
                <p className="text-gray-700 leading-7">{card.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-serif-elegant text-3xl sm:text-4xl font-bold text-[#722F37] mb-4">
            {t("about.structureTitle")}
          </h2>
          <p className="max-w-3xl text-gray-700 leading-7 mb-10">
            {t("about.structureIntro")}
          </p>

          <div className="space-y-6">
            {structureItems.map((item, index) => (
              <div
                key={item.title}
                className="flex gap-5 rounded-3xl border border-[#C9A227]/10 bg-[#f7f1e8] p-6 sm:p-8"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#722F37] text-white font-semibold">
                  {index + 1}
                </div>
                <div>
                  <h3 className="font-serif-elegant text-2xl font-bold text-[#722F37] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-700 leading-7">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#f7f1e8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-serif-elegant text-3xl sm:text-4xl font-bold text-[#722F37] mb-6">
            {t("about.purposeTitle")}
          </h2>
          <div className="rounded-[2rem] border border-[#C9A227]/20 bg-[#722F37] p-8 sm:p-10 text-white">
            <p className="text-lg leading-8 mb-6">{t("about.purposeBody")}</p>

            <ul className="space-y-4 text-white/90">
              {purposePoints.map((point) => (
                <li key={point} className="flex items-start gap-3 leading-7">
                  <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-[#C9A227]" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#FDF8F3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-serif-elegant text-3xl sm:text-4xl font-bold text-[#722F37] mb-6">
            {t("about.closingTitle")}
          </h2>
          <p className="text-gray-700 leading-8">{t("about.closingBody")}</p>
        </div>
      </section>
    </main>
  );
}
